import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiCallError, callFunction } from '@/lib/api';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

export function ForgotPassword() {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      // Routes through the rate-limited request-password-reset Edge Function.
      // The EF always responds with ok:true regardless of whether the email
      // exists, so we never leak account existence on this endpoint.
      await callFunction<{ email: string }, { ok: true }>('request-password-reset', {
        email,
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiCallError && err.code === 'invalid-input') {
        setError(t('auth.forgot_password.invalid_email'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title={t('auth.forgot_password.sent_title')}
        subtitle={t('auth.forgot_password.sent_body')}
        footer={
          <Link to="/sign-in" className="font-medium text-indigo-600 hover:text-indigo-500">
            {t('auth.forgot_password.back_to_sign_in')}
          </Link>
        }
      >
        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
          {t('auth.forgot_password.sent_body')}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('auth.forgot_password.title')}
      subtitle={t('auth.forgot_password.subtitle')}
      footer={
        <Link to="/sign-in" className="font-medium text-indigo-600 hover:text-indigo-500">
          {t('auth.forgot_password.back_to_sign_in')}
        </Link>
      }
    >
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('auth.forgot_password.email')}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.forgot_password.submitting') : t('auth.forgot_password.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
